import type { Condition } from "building-types/conditions/conditionTypes";
import { type DocOptionSet } from "building-types/options/optionSet";
import type { Table } from "common/data/table";
import { parseFormulaIntoConditions, sanitizeConditionOptionSetId } from "doc/formula/formulaConditions";
import { isBooleanOptionSet } from "doc/options/optionSetBaseUtils";

// keeping this downstream to avoid circular deps
export function getReadableFormula(formula: string, optionSets: Table<DocOptionSet>): string {
  const { conditions, conditionGroups } = parseFormulaIntoConditions(formula);
  if (!conditions) {
    return "";
  }

  const allConditions: string[] = [];
  if (conditions.length > 0) {
    allConditions.push(getReadableConditions(conditions, optionSets));
  }

  const readableConditionGroups: string[] = [];
  for (const group of Object.values(conditionGroups ?? [])) {
    readableConditionGroups.push(group.connective ?? "");
    if (group.conditions.length > 0) {
      readableConditionGroups.push(`(${getReadableConditions(group.conditions, optionSets)})`);
    }
  }

  if (readableConditionGroups.length > 0) {
    allConditions.push(readableConditionGroups.join(" "));
  }

  return allConditions
    .filter((x) => !!x)
    .join(" ")
    .trim();
}

function getReadableConditions(conditions: Condition[], optionSets: Table<DocOptionSet>): string {
  const readableConditions: string[] = [];
  for (const condition of conditions) {
    readableConditions.push(getReadableCondition(condition, optionSets));
  }

  return readableConditions.join(" ");
}

export function getReadableCondition(condition: Condition, optionSets: Table<DocOptionSet>): string {
  let readableCondition = "";
  const optionSetId = sanitizeConditionOptionSetId(condition);
  const optionSet = optionSets[sanitizeConditionOptionSetId(condition)];
  if (!optionSet) {
    console.warn(`Option Set not found for optionSetId "${optionSetId}"`);
    return "";
  }

  const option = condition.value ? optionSet.options[condition.value.toString()] : undefined;
  if (!option) {
    return "";
  }

  readableCondition += condition.connective ? `${condition.connective} ` : "";
  readableCondition += `"${optionSet.name}"`;
  if (isBooleanOptionSet(optionSet)) {
    readableCondition += isBooleanConditionTrue(condition) ? " is enabled " : " is disabled ";
  } else {
    readableCondition += condition.operator === "==" ? " is set to " : " is not set to ";
    readableCondition += `"${option.name}"`;
  }
  return readableCondition.trim();
}

function isBooleanConditionTrue(condition: Condition): boolean {
  if (condition.operator === "==" && condition.value === "true") {
    return true;
  }
  if (condition.operator === "!=" && condition.value === "false") {
    return true;
  }
  return false;
}
